import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Popover,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DeleteConfirmationModal from "../deleteConfirmationModal";
import TableHeader from "../table-header";
import { catalogPlanService } from "../../utils/services/catalogPlanService";
import { toast } from "react-toastify";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "../../globalStyles/icons/deleteIcon";
import EditIcon from "../../globalStyles/icons/editIcon";
import { APP_LOCALE } from "../../utils/formatDate";

// ─── Styled components ────────────────────────────────────────────────────────

const StyledTableCell = styled(TableCell)({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    border: "none",
    padding: 8,
  },
  [`&.${tableCellClasses.body}`]: { fontSize: 12, padding: 6 },
});

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "var(--bg-paper)",
    border: "1px solid var(--border-color)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "var(--row-bg-even)",
    border: "1px solid var(--border-color)",
  },
  "&:last-child td, &:last-child th": { border: 0 },
  cursor: "pointer",
  "&:hover": { opacity: 0.85 },
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtCurrency = (v) =>
  v == null ? "–" : `$${Math.round(v / 1000).toLocaleString()}K`;

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(APP_LOCALE, { dateStyle: "medium" })
    : "–";

const STATUS_CFG = {
  active: { label: "Active", bg: "#e8f5e9", color: "#2e7d32" },
  approved: { label: "Approved", bg: "#e8f5e9", color: "#2e7d32" },
  review: { label: "In Review", bg: "#fff8e1", color: "#e65100" },
  draft: {
    label: "Draft",
    bg: "var(--bg-secondary)",
    color: "var(--text-secondary)",
  },
};

const ActionPopover = ({ row, onEdit, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const openPopover = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const closePopover = () => setAnchorEl(null);
  const doAction = (type, e) => {
    if (e) e.stopPropagation();
    closePopover();
    if (type === "edit") onEdit(row);
    if (type === "delete") onDelete(row);
  };

  return (
    <Box>
      <IconButton
        size="small"
        onClick={openPopover}
        sx={{ color: open ? "primary.main" : "inherit" }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box display="flex" flexDirection="column" p={1}>
          <Button
            onClick={(e) => doAction("edit", e)}
            sx={{ textTransform: "none" }}
          >
            <Box display="flex" alignItems="center" gap={1} width="100%">
              <EditIcon />
              <Typography
                fontFamily="Poppins"
                fontSize={14}
                fontWeight={500}
                color="var(--text-tertiary)"
              >
                Edit
              </Typography>
            </Box>
          </Button>
          <Button
            onClick={(e) => doAction("delete", e)}
            sx={{ textTransform: "none" }}
          >
            <Box display="flex" alignItems="center" gap={1} width="100%">
              <DeleteIcon />
              <Typography
                fontFamily="Poppins"
                fontSize={14}
                fontWeight={500}
                color="var(--text-tertiary)"
              >
                Delete
              </Typography>
            </Box>
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const CatalogPlanList = ({
  data,
  loading,
  clientId,
  onEditData,
  onDeleteData,
}) => {
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await catalogPlanService.delete(deleteTarget._id);
      toast.success("Plan deleted successfully!");
      onDeleteData?.();
    } catch (err) {
      toast.error("Failed to delete plan.");
      console.error(err);
    } finally {
      setDeleteLoading(false);
      setDeleteTarget(null);
    }
  };

  const TableSchema = [
    { key: "name", title: "Name" },
    { key: "year", title: "Year" },
    { key: "status", title: "Status" },
    { key: "total_budget", title: "Total Budget" },
    { key: "scenario", title: "Scenario" },
    { key: "dimension", title: "Plan By" },
    { key: "createdAt", title: "Created" },
    { key: "action", title: "" },
  ];

  const colCount = TableSchema.length;

  return (
    <>
      {deleteLoading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(0,0,0,0.4)",
            zIndex: 9999,
          }}
        >
          <CircularProgress color="inherit" sx={{ color: "#fff" }} />
        </Box>
      )}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "1px solid var(--border-color)",
          overflowY: "auto",
          maxHeight: "100%",
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{ border: "1px solid var(--border-color)" }}
        >
          <TableHead>
            <StyledTableRow>
              {TableSchema.map((col) => (
                <TableHeader key={col.key} title={col.title} />
              ))}
            </StyledTableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              [...Array(15)].map((_, ri) => (
                <TableRow key={ri}>
                  {[...Array(colCount)].map((_, ci) => (
                    <TableCell key={ci} sx={{ p: 2 }}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : !data || data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                  <Typography
                    fontFamily="Poppins"
                    fontSize={13}
                    color="text.secondary"
                  >
                    No plans found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((plan) => {
                const statusCfg = STATUS_CFG[plan.status] || STATUS_CFG.draft;
                return (
                  <StyledTableRow
                    key={plan._id}
                    onClick={() => onEditData?.(plan)}
                  >
                    <StyledTableCell>
                      <Typography
                        fontFamily="Poppins"
                        fontSize={13}
                        fontWeight={600}
                        color="var(--text-primary)"
                      >
                        {plan.name}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography fontFamily="Poppins" fontSize={12}>
                        {plan.year}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Chip
                        label={statusCfg.label}
                        size="small"
                        sx={{
                          bgcolor: statusCfg.bg,
                          color: statusCfg.color,
                          fontWeight: 600,
                          fontSize: 11,
                          height: 20,
                          fontFamily: "Poppins",
                        }}
                      />
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography fontFamily="Poppins" fontSize={12}>
                        {fmtCurrency(plan.total_budget)}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell sx={{ textTransform: "capitalize" }}>
                      <Typography fontFamily="Poppins" fontSize={12}>
                        {(plan.scenario || "").replace(/-/g, " ")}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography fontFamily="Poppins" fontSize={12}>
                        {plan.dimension === "product_type"
                          ? "Product Type"
                          : "Category"}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography fontFamily="Poppins" fontSize={12}>
                        {fmtDate(plan.createdAt)}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell
                      align="right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ActionPopover
                        row={plan}
                        onEdit={onEditData}
                        onDelete={
                          onDeleteData ? () => setDeleteTarget(plan) : null
                        }
                      />
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <DeleteConfirmationModal
        open={Boolean(deleteTarget)}
        handleClose={() => setDeleteTarget(null)}
        handleDelete={handleDelete}
        title={`Delete plan "${deleteTarget?.name}"?`}
      />
    </>
  );
};

export default CatalogPlanList;
